/* eslint-disable import/no-restricted-paths */
import { useEffect, useRef, useState } from "react";

import { Loader } from "@/components/Loader";
import { useFeedbackTool } from "@/features/request/feedback-tool/feedbackTool/hooks/useFeedbackTool.tsx";
import usePdfToImages from "@/features/request/feedback-tool/feedbackTool/hooks/usePdfToImages";
import { usePdfPinchZoom } from "@/features/request/feedback-tool/feedbackTool/pdf/hooks/usePdfPinchZoom";

import "./style.css";

export default function PdfViewer({
  attachmentId,
  url,
}: {
  attachmentId: number;
  url: string;
}) {
  const [page, setPage] = useState(1);
  const { imageUrls } = usePdfToImages({ attachmentId, pdfUrl: url });
  const { isTablet, pdfContainerRef } = useFeedbackTool();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const imgRefs = useRef<Map<string, HTMLImageElement>>(new Map());

  usePdfPinchZoom({
    contentRef: pdfContainerRef,
    enabled: isTablet,
    hScrollerRef: scrollerRef,
    vScrollerRef: scrollerRef,
  });

  const setImgsRef = (id: string, element: HTMLImageElement | null) => {
    if (element) {
      imgRefs.current.set(id, element);
    } else {
      imgRefs.current.delete(id);
    }
  };

  useEffect(() => {
    if (page) {
      const selectedRef = imgRefs.current?.get((page - 1).toString());

      if (selectedRef) {
        setTimeout(() => {
          selectedRef.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 100);
      }
    }
  }, [page]);

  return (
    <div
      className={`h-full ${isTablet ? "overflow-auto" : ""}`}
      ref={scrollerRef}
    >
      {imageUrls.length === 0 ? (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ">
          <Loader className="bg-bg-transparent" />
        </div>
      ) : null}
      <div className="flex">
        <div className="hidden lg:flex w-40 h-screen flex-col items-center overflow-auto fixed top-20">
          {imageUrls.map((url, index) => (
            <div
              className={`${page === index + 1 ? "bg-gray-400" : ""} cursor-pointer`}
              key={url + "-" + index}
              onClick={() => setPage(index + 1)}
            >
              <img
                alt={`Page ${index + 1}`}
                className="m-2 w-30"
                key={index}
                src={url}
              />
            </div>
          ))}
        </div>
        <div
          className={`pt-1 overflow-x-hidden lg:pl-40 ${isTablet ? "w-full shrink-0" : ""}`}
          ref={pdfContainerRef}
        >
          {imageUrls.map((url, index) => (
            <img
              alt={`Page ${index + 1}`}
              className={`pl-2 pr-2 pt-1 pb-1 h-auto max-w-full ${isTablet ? "w-full" : "w-auto"}`}
              key={index}
              ref={(el) => setImgsRef(index.toString(), el)}
              src={url}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
